import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import EcommerceNav from '../../components/ecommerce/EcommerceNav';
import EcommerceFooter from '../../components/ecommerce/EcommerceFooter';
import RazorpayPaymentModal from '../../components/ecommerce/RazorpayPaymentModal';
import './Ecommerce.css';
import './Checkout.css';
// countriesStates import removed (unused)
import { formatINR } from '../../utils/currency';

const Checkout = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    shippingAddress: {
      firstName: '',
      lastName: '',
      company: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
      phone: ''
    },
    billingAddress: {
      firstName: '',
      lastName: '',
      company: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
      phone: ''
    },
    paymentMethod: 'razorpay',
    shippingMethod: 'standard'
  });
  const [useSameAddress, setUseSameAddress] = useState(true);
  const [showUpiModal, setShowUpiModal] = useState(false);
  const [upiQrData, setUpiQrData] = useState('');
  const [showRazorpayModal, setShowRazorpayModal] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState(null);

  const fetchCart = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/ecommerce/cart');
      
      if (response.data.success) {
        setCart(response.data.data);
        if (response.data.data.items.length === 0) {
          navigate('/ecommerce/cart');
        }
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
      navigate('/ecommerce/cart');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (user) {
      fetchCart();
    } else {
      navigate('/login');
    }
  }, [user, navigate, fetchCart]);

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleUseSameAddressChange = (checked) => {
    setUseSameAddress(checked);
    if (checked) {
      setFormData(prev => ({
        ...prev,
        billingAddress: { ...prev.shippingAddress }
      }));
    }
  };


  const calculateTotals = () => {
    if (!cart) return { subtotal: 0, shipping: 0, tax: 0, total: 0 };

    const subtotal = cart.totalAmount;
    const shipping = formData.shippingMethod === 'express' ? 15 : 5;
    const tax = subtotal * 0.1; // 10% tax
    const total = subtotal + shipping + tax;

    return { subtotal, shipping, tax, total };
  };


  const handleRazorpaySuccess = (paymentData) => {
    // Navigate to order success page
    if (createdOrderId) {
      navigate(`/ecommerce/orders/${createdOrderId}`);
    } else {
      navigate('/ecommerce/orders');
    }
  };

  const handleRazorpayError = (error) => {
    alert(error || 'Payment failed. Please try again.');
    setShowRazorpayModal(false);
  };

  const openUpiModal = () => {
    // Basic UPI QR placeholder; replaced later when order is created
    const recipient = 'e-bharat@upi';
    const amount = calculateTotals().total || 1;
    const payeeName = 'E-Bharat';
    const txnNote = 'Online Payment';
    const upiUrl = `upi://pay?pa=${encodeURIComponent(recipient)}&pn=${encodeURIComponent(payeeName)}&tn=${encodeURIComponent(txnNote)}&am=${encodeURIComponent(String(amount))}&cu=INR`;
    setUpiQrData(upiUrl);
    setShowUpiModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!useSameAddress) {
      // Validate billing address
      const requiredFields = ['firstName', 'lastName', 'address1', 'city', 'state', 'zipCode'];
      const missingFields = requiredFields.filter(field => !formData.billingAddress[field]);
      if (missingFields.length > 0) {
        alert('Please fill in all required billing address fields');
        return;
      }
    }

    try {
      setSubmitting(true);
      
      const orderData = {
        ...formData,
        billingAddress: useSameAddress ? formData.shippingAddress : formData.billingAddress
      };

      const response = await api.post('/api/ecommerce/orders', orderData);
      if (!response.data.success) throw new Error('Order creation failed');
      const createdOrder = response.data.data;

      if (formData.paymentMethod === 'cod') {
        navigate(`/ecommerce/orders/${createdOrder._id}`);
        return;
      }

      if (formData.paymentMethod === 'razorpay') {
        // Order already created, show Razorpay modal
        setCreatedOrderId(createdOrder._id);
        setShowRazorpayModal(true);
        return;
      }

      // Other payment methods
      navigate(`/ecommerce/orders/${createdOrder._id}`);
    } catch (error) {
      console.error('Error creating order:', error);
      const msg = error?.response?.data?.message || error?.message || 'Error creating order. Please try again.';
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (price) => formatINR(price);

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="checkout-container">
        <div className="loading">Loading checkout...</div>
      </div>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="checkout-container">
        <div className="error">Your cart is empty</div>
      </div>
    );
  }

  return (
    <div className="ecommerce-page">
      <EcommerceNav />
      <div className="checkout-container">
        <div className="checkout-header">
        <h1>Checkout</h1>
        <p>Complete your order</p>
      </div>

      <form onSubmit={handleSubmit} className="checkout-form">
        <div className="checkout-content">
          <div className="checkout-main">
            {/* Shipping Address */}
            <div className="checkout-section">
              <h2>Shipping Address</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={formData.shippingAddress.firstName}
                    onChange={(e) => handleInputChange('shippingAddress', 'firstName', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={formData.shippingAddress.lastName}
                    onChange={(e) => handleInputChange('shippingAddress', 'lastName', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Company</label>
                  <input
                    type="text"
                    value={formData.shippingAddress.company}
                    onChange={(e) => handleInputChange('shippingAddress', 'company', e.target.value)}
                  />
                </div>
                <div className="form-group full-width">
                  <label>Address Line 1 *</label>
                  <input
                    type="text"
                    value={formData.shippingAddress.address1}
                    onChange={(e) => handleInputChange('shippingAddress', 'address1', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group full-width">
                  <label>Address Line 2</label>
                  <input
                    type="text"
                    value={formData.shippingAddress.address2}
                    onChange={(e) => handleInputChange('shippingAddress', 'address2', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    value={formData.shippingAddress.city}
                    onChange={(e) => handleInputChange('shippingAddress', 'city', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>State *</label>
                  <input
                    type="text"
                    value={formData.shippingAddress.state}
                    onChange={(e) => handleInputChange('shippingAddress', 'state', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>ZIP Code *</label>
                  <input
                    type="text"
                    value={formData.shippingAddress.zipCode}
                    onChange={(e) => handleInputChange('shippingAddress', 'zipCode', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Country *</label>
                  <input
                    type="text"
                    placeholder="Enter country name"
                    value={formData.shippingAddress.country}
                    onChange={(e) => handleInputChange('shippingAddress', 'country', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={formData.shippingAddress.phone}
                    onChange={(e) => handleInputChange('shippingAddress', 'phone', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Billing Address */}
            <div className="checkout-section">
              <div className="section-header">
                <h2>Billing Address</h2>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={useSameAddress}
                    onChange={(e) => handleUseSameAddressChange(e.target.checked)}
                  />
                  Same as shipping address
                </label>
              </div>

              {!useSameAddress && (
                <div className="form-grid">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      value={formData.billingAddress.firstName}
                      onChange={(e) => handleInputChange('billingAddress', 'firstName', e.target.value)}
                      required={!useSameAddress}
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      value={formData.billingAddress.lastName}
                      onChange={(e) => handleInputChange('billingAddress', 'lastName', e.target.value)}
                      required={!useSameAddress}
                    />
                  </div>
                  <div className="form-group">
                    <label>Company</label>
                    <input
                      type="text"
                      value={formData.billingAddress.company}
                      onChange={(e) => handleInputChange('billingAddress', 'company', e.target.value)}
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Address Line 1 *</label>
                    <input
                      type="text"
                      value={formData.billingAddress.address1}
                      onChange={(e) => handleInputChange('billingAddress', 'address1', e.target.value)}
                      required={!useSameAddress}
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Address Line 2</label>
                    <input
                      type="text"
                      value={formData.billingAddress.address2}
                      onChange={(e) => handleInputChange('billingAddress', 'address2', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>City *</label>
                    <input
                      type="text"
                      value={formData.billingAddress.city}
                      onChange={(e) => handleInputChange('billingAddress', 'city', e.target.value)}
                      required={!useSameAddress}
                    />
                  </div>
                  <div className="form-group">
                    <label>State *</label>
                    <input
                      type="text"
                      value={formData.billingAddress.state}
                      onChange={(e) => handleInputChange('billingAddress', 'state', e.target.value)}
                      required={!useSameAddress}
                    />
                  </div>
                  <div className="form-group">
                    <label>ZIP Code *</label>
                    <input
                      type="text"
                      value={formData.billingAddress.zipCode}
                      onChange={(e) => handleInputChange('billingAddress', 'zipCode', e.target.value)}
                      required={!useSameAddress}
                    />
                  </div>
                  <div className="form-group">
                    <label>Country *</label>
                    <input
                      type="text"
                      placeholder="Enter country name"
                      value={formData.billingAddress.country}
                      onChange={(e) => handleInputChange('billingAddress', 'country', e.target.value)}
                      required={!useSameAddress}
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={formData.billingAddress.phone}
                      onChange={(e) => handleInputChange('billingAddress', 'phone', e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Payment Method */}
            <div className="checkout-section">
              <h2>Payment Method</h2>
            <div className="payment-methods">
              <label className="payment-method">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="razorpay"
                  checked={formData.paymentMethod === 'razorpay'}
                  onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                />
                <div className="payment-option">
                  <span className="payment-icon">💳</span>
                  <span>Pay with Razorpay (Cards, UPI, Wallets)</span>
                </div>
              </label>
              <label className="payment-method">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="cod"
                  checked={formData.paymentMethod === 'cod'}
                  onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                />
                <div className="payment-option">
                  <span className="payment-icon">💰</span>
                  <span>Cash on Delivery</span>
                </div>
              </label>
            </div>
            </div>

            {/* Shipping Method */}
            <div className="checkout-section">
              <h2>Shipping Method</h2>
              <div className="shipping-methods">
                <label className="shipping-method">
                  <input
                    type="radio"
                    name="shippingMethod"
                    value="standard"
                    checked={formData.shippingMethod === 'standard'}
                    onChange={(e) => setFormData({ ...formData, shippingMethod: e.target.value })}
                  />
                  <div className="shipping-info">
                    <span className="shipping-name">Standard Shipping</span>
                    <span className="shipping-price">$5.00</span>
                    <span className="shipping-time">5-7 business days</span>
                  </div>
                </label>
                <label className="shipping-method">
                  <input
                    type="radio"
                    name="shippingMethod"
                    value="express"
                    checked={formData.shippingMethod === 'express'}
                    onChange={(e) => setFormData({ ...formData, shippingMethod: e.target.value })}
                  />
                  <div className="shipping-info">
                    <span className="shipping-name">Express Shipping</span>
                    <span className="shipping-price">$15.00</span>
                    <span className="shipping-time">2-3 business days</span>
                  </div>
                </label>
              </div>
            </div>

          </div>

          {/* Order Summary */}
          <div className="checkout-sidebar">
            <div className="order-summary">
              <h3>Order Summary</h3>
              
              <div className="order-items">
                {cart.items.map((item) => (
                  <div key={item._id} className="order-item">
                    <div className="item-image">
                      {item.product.inventory.images && item.product.inventory.images.length > 0 ? (
                        <img
                          src={item.product.inventory.images[0]}
                          alt={item.product.inventory.itemName}
                          onError={(e) => {
                            e.target.src = '/placeholder-product.jpg';
                          }}
                        />
                      ) : (
                        <div className="placeholder-image">No Image</div>
                      )}
                    </div>
                    <div className="item-details">
                      <h4>{item.product.inventory.itemName}</h4>
                      <p>Qty: {item.quantity}</p>
                    </div>
                    <div className="item-price">
                      {formatPrice(item.price * item.quantity)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="order-totals">
                <div className="total-row">
                  <span>Subtotal</span>
                  <span>{formatPrice(totals.subtotal)}</span>
                </div>
                <div className="total-row">
                  <span>Shipping</span>
                  <span>{formatPrice(totals.shipping)}</span>
                </div>
                <div className="total-row">
                  <span>Tax</span>
                  <span>{formatPrice(totals.tax)}</span>
                </div>
                <div className="total-row final">
                  <span>Total</span>
                  <span>{formatPrice(totals.total)}</span>
                </div>
              </div>

              <button
                type="submit"
                className="place-order-btn"
                disabled={submitting}
              >
                {submitting ? 'Processing...' : 'Place Order'}
              </button>
            </div>
          </div>
        </div>
      </form>
      </div>
      
      {/* Razorpay Payment Modal */}
      {showRazorpayModal && createdOrderId && (
        <RazorpayPaymentModal
          isOpen={showRazorpayModal}
          onClose={() => setShowRazorpayModal(false)}
          amount={totals.total}
          orderId={createdOrderId}
          onSuccess={handleRazorpaySuccess}
          onError={handleRazorpayError}
        />
      )}
      
      <EcommerceFooter />
    </div>
  );
};

export default Checkout;
